/**
 * IP-based geo detection helper.
 *
 * Calls `/api/geo/detect` once per browser session (or per 24h), caches the
 * result in `localStorage`, and exposes a synchronous getter so any component
 * can read the visitor's country + currency without pulling in a hook.
 *
 * Usage:
 *   import { fetchGeo, getGeo } from "../lib/geo";
 *   useEffect(() => { fetchGeo(); }, []);   // fire-and-forget on app mount
 *   const { country, currency } = getGeo(); // sync read anywhere
 */
import api from "./api";

const STORAGE_KEY = "haulhub_geo";
const OVERRIDE_KEY = "haulhub_currency_pref"; // user's manual currency choice
const TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

// Curated list of currencies users can override to. Mirrors what the server's
// COUNTRY_CURRENCY map exposes; we keep it small on purpose so users don't
// scroll through 60 options — they can always leave it on Auto.
export const OVERRIDABLE_CURRENCIES = [
  { code: "usd", symbol: "$", label: "USD · US Dollar" },
  { code: "eur", symbol: "€", label: "EUR · Euro" },
  { code: "gbp", symbol: "£", label: "GBP · British Pound" },
  { code: "ils", symbol: "₪", label: "ILS · Israeli Shekel" },
  { code: "try", symbol: "₺", label: "TRY · Turkish Lira" },
  { code: "aed", symbol: "AED", label: "AED · UAE Dirham" },
  { code: "rub", symbol: "₽", label: "RUB · Russian Ruble" },
  { code: "sar", symbol: "SAR", label: "SAR · Saudi Riyal" },
  { code: "inr", symbol: "₹", label: "INR · Indian Rupee" },
  { code: "brl", symbol: "R$", label: "BRL · Brazilian Real" },
  { code: "mxn", symbol: "MX$", label: "MXN · Mexican Peso" },
  { code: "cad", symbol: "C$", label: "CAD · Canadian Dollar" },
  { code: "aud", symbol: "A$", label: "AUD · Australian Dollar" },
];

/** Read the cached geo blob. Returns null if none exists or it's expired. */
function readCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.country || !parsed.fetched_at) return null;
    if (Date.now() - parsed.fetched_at > TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(country, currency, source, supported) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      country, currency, source,
      supported: supported !== undefined ? supported : false,
      fetched_at: Date.now(),
    }));
  } catch {
    /* private mode → silently ignore */
  }
}

let _inflight = null;

/**
 * Fetch the visitor's country + currency. Idempotent: repeat calls within the
 * same session share one in-flight promise; cached results skip the network
 * entirely.
 *
 * Returns `{ country, currency, source, supported }`. Never throws — on failure
 * it resolves with `{ country: "US", currency: "usd", source: "fallback", supported: true }`.
 */
export async function fetchGeo() {
  const cached = readCache();
  if (cached) return applyOverride(cached);
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const { data } = await api.get("/geo/detect");
      const country = (data.country || "US").toUpperCase();
      const currency = (data.currency || "usd").toLowerCase();
      const supported = data.supported === true;
      writeCache(country, currency, data.source || "lookup", supported);
      return applyOverride({ country, currency, source: data.source || "lookup", supported });
    } catch {
      const fallback = { country: "US", currency: "usd", source: "fallback", supported: true };
      writeCache(fallback.country, fallback.currency, fallback.source, fallback.supported);
      return applyOverride(fallback);
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

/**
 * Overlay the user's manual currency override on top of geo detection.
 * Country stays as detected (used for pricing tables + waitlist); only
 * currency swaps to the user's chosen display currency.
 */
function applyOverride(geo) {
  if (!geo) return geo;
  const override = getCurrencyOverride();
  if (override) {
    return { ...geo, currency: override, override: true };
  }
  return geo;
}

/**
 * Synchronous read of the cached geo blob. Returns `null` if we haven't
 * detected yet — callers should treat that as "no default, keep loading".
 */
export function getGeo() {
  const cached = readCache();
  return cached ? applyOverride(cached) : null;
}

/** Get the user's manual currency override, or null if none set. */
export function getCurrencyOverride() {
  try {
    const v = localStorage.getItem(OVERRIDE_KEY);
    return v ? v.toLowerCase() : null;
  } catch {
    return null;
  }
}

/** Set (or clear with null/undefined) the user's manual currency override. */
export function setCurrencyOverride(currency) {
  try {
    if (currency) {
      localStorage.setItem(OVERRIDE_KEY, currency.toLowerCase());
    } else {
      localStorage.removeItem(OVERRIDE_KEY);
    }
    // Broadcast so open tabs/components can re-render with the new currency.
    window.dispatchEvent(new CustomEvent("drivon:currency-changed", {
      detail: { currency: currency ? currency.toLowerCase() : null },
    }));
  } catch { /* ignore */ }
}

/** Force-clear the geo cache. Used by settings UI when the user picks a country
 *  manually — we don't want the auto-detect to overwrite their preference on
 *  the next visit. */
export function clearGeoCache() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}


/**
 * Client-side offline country lookup by bounding box. Covers the three
 * markets Drivon actively supports (IL, AE, TR). Used to derive the
 * customer's country from the CURRENT pickup coords instead of a stale
 * IP-based geo cache (e.g. Emergent Preview infra is US-hosted, so the
 * IP geo cache lies about the user's real location whenever they're
 * booking a trip in IL/AE/TR).
 *
 * Bounding boxes are conservative on the ocean/desert edges — a coord
 * that falls outside all three returns null and the caller should fall
 * back to its next-best signal (fare?.country, then getGeo() cache).
 */
const COUNTRY_BBOXES = [
  // Israel — mainland incl. West Bank + Golan.
  { cc: "IL", latMin: 29.3, latMax: 33.4, lngMin: 34.2, lngMax: 35.9 },
  // United Arab Emirates — all 7 emirates + coastal islands.
  { cc: "AE", latMin: 22.5, latMax: 26.5, lngMin: 51.3, lngMax: 56.7 },
  // Turkey — European + Anatolian mainland incl. Istanbul.
  { cc: "TR", latMin: 35.8, latMax: 42.5, lngMin: 25.7, lngMax: 45.5 },
];

export function countryFromCoords(lat, lng) {
  if (lat == null || lng == null) return null;
  const la = Number(lat), lo = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(lo)) return null;
  for (const b of COUNTRY_BBOXES) {
    if (la >= b.latMin && la <= b.latMax && lo >= b.lngMin && lo <= b.lngMax) {
      return b.cc;
    }
  }
  return null;
}
